import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, FileText, ShieldCheck, Clock, MapPin } from "lucide-react";

const HERO_IMG = "/assets/job6.webp";

const stats = [
  { icon: ShieldCheck, label: "Safety-First Crews" },
  { icon: Clock, label: "Fast Mobilization" },
  { icon: MapPin, label: "Greater Houston Area" },
];

const lineVariants = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: 0,
    transition: { duration: 0.9, delay: 0.15 + i * 0.13, ease: [0.16, 1, 0.3, 1] },
  }),
};

function MaskedLine({ children, i }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span className="block" custom={i} variants={lineVariants} initial="hidden" animate="show">
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section id="home" ref={ref} className="relative min-h-screen flex items-center noise-overlay overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-0 scale-110" style={{ y: bgY }} aria-hidden="true">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_IMG})` }} />
        <div className="absolute inset-0 bg-[#0F172A]/85" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-40 pb-24 w-full">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="text-base lg:text-lg font-bold uppercase tracking-[0.25em] text-[#FF5A00] mb-6 font-mono-tech text-center"
          data-testid="hero-overline"
        >
          Houston, Texas • Subsurface Investigation Services
        </motion.p>

        <h1 className="font-heading text-2xl sm:text-3xl lg:text-5xl font-black uppercase tracking-tighter text-white leading-[0.95] max-w-3xl mx-auto text-center" data-testid="hero-heading">
          <MaskedLine i={0}>
            Coring <span className="text-[#FF5A00]">•</span> Boring
          </MaskedLine>
          <MaskedLine i={1}>
            <span className="text-[#FF5A00]">•</span> Subsurface
          </MaskedLine>
          <MaskedLine i={2}>Solutions</MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.65, ease: "easeOut" }}
          className="mt-8 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl mx-auto text-center"
          data-testid="hero-subcopy"
        >
          Aegis Subsurface LLC delivers precision asphalt coring, concrete coring, geotechnical boring, and soil
          sampling for engineering firms, contractors, developers, and municipalities across Greater Houston.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8, ease: "easeOut" }}
          className="mt-10 flex flex-col sm:flex-row gap-4 justify-center"
        >
          <a
            href="#quote"
            data-testid="hero-quote-button"
            className="inline-flex items-center justify-center gap-2 bg-[#FF5A00] hover:bg-[#E04F00] text-white font-heading font-bold uppercase tracking-wider px-8 py-4 rounded-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-[6px_6px_0px_#ffffff26]"
          >
            <FileText className="w-5 h-5" />
            Request a Quote
          </a>
          <a
            href="tel:+18326611121"
            data-testid="hero-call-button"
            className="inline-flex items-center justify-center gap-2 border-2 border-white/70 text-white hover:bg-white hover:text-[#0F172A] font-heading font-bold uppercase tracking-wider px-8 py-4 rounded-sm transition-colors duration-200"
          >
            <Phone className="w-5 h-5" />
            Call Now
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.95, ease: "easeOut" }}
          className="mt-16 grid grid-cols-1 sm:grid-cols-3 border border-white/15 divide-y sm:divide-y-0 sm:divide-x divide-white/15"
          data-testid="hero-stats"
        >
          {stats.map((s) => (
            <div key={s.label} className="flex items-center gap-3 px-6 py-5 bg-white/5 backdrop-blur-sm">
              <s.icon className="w-6 h-6 text-[#FF5A00] shrink-0" />
              <span className="text-sm font-semibold uppercase tracking-wider text-white">{s.label}</span>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
